import React, { useContext } from 'react'

import type { Deposits } from '@awaze/mfe-common.configs.brand'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awaze/mfe-common.contexts.tms'
import {
  AccommodationType,
  CommonSearchResult,
  CommonSite,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { ImagesItem } from '@awaze/mfe-common.types-and-mocks.site-search-service/types/properties.types'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { IWishlistButtonProps } from '@awazevr/design-system.compositions.property-card'
import { Image } from '@awazevr/design-system.compositions.property-list/dist/property-list.types'

import { PropertyCardNewDesign } from '@/__experimental__/PropertyCardNewDesign/PropertyCardNewDesign'
import { PropertyCardSkeleton } from '@/__experimental__/PropertyCardNewDesign/skeleton/PropertyCardSkeleton'
import { OPTIMIZELY_EVENTS, RESULTS_PAGE_SIZE } from '@/constants'
import { removeFloorplanImagesFromImageArray } from '@/containers/SearchResults/helpers'
import { formatter } from '@/containers/SearchResults/propertyFormatter.util'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { PropertyCardProps } from '@/experiment/PropertyCardNewDesign/PropertyCardNewDesign'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

import { StyledPropertyList } from './property-list.style'

export interface PropertyListCompositeProps {
  properties: CommonSite[]
  translations: PropertyCardProps['translations']
  deposits?: Deposits
  config: {
    brand: Brand
    searchCriteria: SearchCriteria
    searchData: CommonSearchResult
    searchParameters: string
    siteSearch: boolean
    dateToday: Date
    accommodationType: AccommodationType
    pdpUrlSegment: string
    locale: string
    datedSearch: boolean
    country: string
    startDate?: string
  }
  onCarouselInteractFactory: (
    images: Array<Image | ImagesItem>,
    displayName: string,
    code: string,
  ) => (
    event: 'open' | 'close' | 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
  getWishlistButtonProps?: (code: string | number) => IWishlistButtonProps
}

export const PropertyListComposite = ({
  properties,
  translations,
  deposits,
  config,
  onCarouselInteractFactory,
  getWishlistButtonProps,
}: PropertyListCompositeProps): React.ReactNode => {
  const tms = useContext(TmsContext)
  const optiContext = useOptimizelyDecisionsContext()
  const searchCriteria = config?.searchCriteria
  const searchData = config.searchData
  const searchParameters = new URLSearchParams(config?.searchParameters)

  const { formatDate, country, formatMessage } = useIntlContext()

  const onPropertyLinkClick = ({ propertyCode }: { propertyCode: string }) => {
    new DataLayerFactory()
      .getEvent(DataLayerEventType.PropertyClick)
      .update(
        tms,
        { propertyCode, searchCriteria, searchData },
        dataLayerErrorHandler,
      )
    trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.PDP_CLICK_THROUGH,
      optiContext,
    )
  }
  return (
    <StyledPropertyList aria-label="properties" role="list">
      {!properties.length &&
        Array(RESULTS_PAGE_SIZE)
          .fill('')
          .map((_, i) => <PropertyCardSkeleton key={i} />)}

      {properties.length > 0 &&
        properties.map((property: CommonSite) => {
          const propertyImagesWithoutFloorplans = property.images
            ? removeFloorplanImagesFromImageArray(property.images)
            : []

          const newProps = formatter({
            property: property,
            brand: config.brand,
            deposits: deposits,
            searchParams: searchParameters,
            datedSearch: config.datedSearch,
            pdpUrlSegment: config.pdpUrlSegment,
            formatDate,
            country,
            formatMessage,
            startDate: config.startDate,
            siteSearch: config.siteSearch,
          })
          const wishlistButton = getWishlistButtonProps
            ? getWishlistButtonProps(property.code)
            : undefined
          return (
            <PropertyCardNewDesign
              key={newProps.code}
              {...newProps}
              wishlistButton={wishlistButton}
              onPropertyLinkClick={() =>
                onPropertyLinkClick({ propertyCode: property.code })
              }
              translations={translations}
              siteSearch={config.siteSearch}
              onCarouselInteractCallback={onCarouselInteractFactory(
                propertyImagesWithoutFloorplans,
                property.displayName ?? '',
                property.code,
              )}
            />
          )
        })}
    </StyledPropertyList>
  )
}
